import { Button } from "@/components/ui/button";
import { Pencil, Trash2, GripVertical } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableRow } from "./SortableRow";

interface PolicyAcquisitionType {
  id: string;
  name: string;
  description: string | null;
}

interface PolicyAcquisitionTableProps {
  policyAcquisitionTypes: PolicyAcquisitionType[];
  onEdit: (type: PolicyAcquisitionType) => void;
  onDelete: (id: string) => void;
  onReorder?: (items: PolicyAcquisitionType[]) => void;
}

export const PolicyAcquisitionTable = ({
  policyAcquisitionTypes,
  onEdit,
  onDelete,
  onReorder,
}: PolicyAcquisitionTableProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = policyAcquisitionTypes.findIndex((type) => type.id === active.id);
      const newIndex = policyAcquisitionTypes.findIndex((type) => type.id === over.id);
      
      const newItems = [...policyAcquisitionTypes];
      const [movedItem] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, movedItem);
      
      onReorder?.(newItems);
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]"></TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Description</TableHead>
          <TableHead className="w-[100px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={policyAcquisitionTypes.map((type) => type.id)}
            strategy={verticalListSortingStrategy}
          >
            {policyAcquisitionTypes.map((type) => (
              <SortableRow
                key={type.id}
                type={type}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </SortableContext>
        </DndContext>
      </TableBody>
    </Table>
  );
};